<div class="w-full max-w-3xl mx-auto">
  <div class="mb-4">
    <h2 class="text-xl font-semibold text-gray-700 dark:text-gray-300">Upload Documents</h2>
    <p class="text-sm text-gray-500 dark:text-gray-400">Supported formats: PDF, DOCX, PPTX, XLSX, TXT, MD</p>
  </div>

  <!-- Drag and drop area -->
  <div 
    (dragover)="onDragOver($event)" 
    (dragleave)="onDragLeave($event)" 
    (drop)="onDrop($event)"
    class="border-2 border-dashed p-8 rounded-lg text-center cursor-pointer transition-all"
    [class.border-blue-300]="!isDragging"
    [class.border-blue-500]="isDragging"
    [class.bg-blue-50]="isDragging"
    [class.dark:border-blue-700]="!isDragging"
    [class.dark:border-blue-500]="isDragging"
    [class.dark:bg-blue-900]="isDragging">
    
    <div class="flex flex-col items-center justify-center">
      <svg xmlns="http://www.w3.org/2000/svg" class="h-12 w-12 text-gray-400 dark:text-gray-500 mb-3" fill="none" viewBox="0 0 24 24" stroke="currentColor">
        <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
      </svg>
      <p class="text-gray-600 dark:text-gray-400 text-lg font-medium">Drag and drop files here</p>
      <p class="text-gray-500 dark:text-gray-500 text-sm mb-4">Or</p>
      <label class="bg-btn-primary hover:opacity-90 text-btn-primary-text py-2 px-4 rounded-md cursor-pointer transition-opacity">
        Browse Files
        <input 
          type="file" 
          multiple 
          class="hidden" 
          (change)="onFileSelected($event)"
          accept=".pdf,.docx,.pptx,.xlsx,.txt,.md,application/pdf,application/vnd.openxmlformats-officedocument.wordprocessingml.document,application/vnd.openxmlformats-officedocument.presentationml.presentation,application/vnd.openxmlformats-officedocument.spreadsheetml.sheet,text/plain,text/markdown">
      </label>
    </div>
  </div>

  <!-- Selected files list -->
  <div *ngIf="files.length > 0" class="mt-6">
    <h3 class="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">Selected Files</h3>
    <ul class="space-y-2">
      <li *ngFor="let file of files; let i = index" class="flex items-center justify-between bg-white dark:bg-gray-800 p-3 rounded-md border border-gray-200 dark:border-gray-700">
        <div class="flex items-center">
          <div class="mr-3">
            <svg xmlns="http://www.w3.org/2000/svg" class="h-6 w-6 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
              <path stroke-linecap="round" stroke-linejoin="round" stroke-width="2" d="M9 12h6m-6 4h6m2 5H7a2 2 0 01-2-2V5a2 2 0 012-2h5.586a1 1 0 01.707.293l5.414 5.414a1 1 0 01.293.707V19a2 2 0 01-2 2z" />
            </svg>
          </div>
          <div>
            <p class="text-sm font-medium text-gray-700 dark:text-gray-300 truncate max-w-xs">{{ file.name }}</p>
            <p class="text-xs text-gray-500 dark:text-gray-400">{{ (file.size / 1024).toFixed(1) }} KB</p>
          </div>
        </div>
        <button 
          (click)="removeFile(i)" 
          class="text-red-500 hover:text-red-700 dark:text-red-400 dark:hover:text-red-300 transition-colors">
          <svg xmlns="http://www.w3.org/2000/svg" class="h-5 w-5" viewBox="0 0 20 20" fill="currentColor">
            <path fill-rule="evenodd" d="M9 2a1 1 0 00-.894.553L7.382 4H4a1 1 0 000 2v10a2 2 0 002 2h8a2 2 0 002-2V6a1 1 0 100-2h-3.382l-.724-1.447A1 1 0 0011 2H9zM7 8a1 1 0 012 0v6a1 1 0 11-2 0V8zm5-1a1 1 0 00-1 1v6a1 1 0 102 0V8a1 1 0 00-1-1z" clip-rule="evenodd" />
          </svg>
        </button>
      </li>
    </ul>
  </div>

  <!-- Upload button and progress -->
  <div class="mt-6">
    <button 
      (click)="uploadFiles()" 
      [disabled]="files.length === 0 || isUploading"
      class="w-full bg-btn-primary hover:opacity-90 disabled:opacity-50 text-btn-primary-text py-2 px-4 rounded-md transition-opacity">
      {{ isUploading ? 'Uploading...' : 'Upload Files' }}
    </button>
  </div>

  <!-- Upload progress -->
  <div *ngIf="fileStatuses.length > 0" class="mt-6">
    <h3 class="text-lg font-medium text-gray-700 dark:text-gray-300 mb-2">Upload Progress</h3>
    <ul class="space-y-3">
      <li *ngFor="let status of fileStatuses" class="bg-white dark:bg-gray-800 p-3 rounded-md border border-gray-200 dark:border-gray-700">
        <div class="flex justify-between mb-1">
          <p class="text-sm font-medium text-gray-700 dark:text-gray-300">{{ status.fileName }}</p>
          <p class="text-sm text-gray-500 dark:text-gray-400">{{ status.progress }}%</p>
        </div>
        <div class="relative w-full h-2 bg-gray-200 dark:bg-gray-700 rounded-full overflow-hidden">
          <div 
            [style.width.%]="status.progress"
            [ngClass]="{
              'bg-blue-500': status.progress < 100 && !status.error,
              'bg-green-500': status.progress === 100 && !status.error,
              'bg-red-500': status.error
            }"
            class="absolute top-0 left-0 h-full transition-all duration-300"></div>
        </div>
        <p *ngIf="status.error" class="text-xs text-red-500 mt-1">{{ status.error }}</p>
      </li>
    </ul>
  </div>
</div>
